import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { agentRadarService } from "../../../services/agentRadarService";
import SaveToListPicker from "./SaveToListPicker";

// "Add to Radar" — the ONE place a player enters an agent's personal radar
// (player profile header, both platforms). Renders next to the Share button.
// Eligibility (flag + Agent/AgencyAdmin role) is gated by HeaderActions; this
// component null-guards a missing player id and owns all radar state.
//
// State comes from GET profile-status on mount. Tapping opens the save-to-list
// picker; checkbox toggles mutate a local draft; Done (or clicking away) commits
// the final list_ids once via save-from-profile. Button state updates
// optimistically — unchecking every list reverts it to "Add to Radar".

// Injects the prototype's pop/sheet keyframes once (no-op if already present).
const ensureAnim = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("tp-radar-anim")) return;
  const el = document.createElement("style");
  el.id = "tp-radar-anim";
  el.textContent =
    "@keyframes tp-pop{from{transform:scale(0.96);opacity:0}to{transform:none;opacity:1}}" +
    "@keyframes tp-sheet{from{transform:translateY(48px);opacity:0}to{transform:none;opacity:1}}";
  document.head.appendChild(el);
};

const sameSet = (a, b) => {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
};

const AddToRadarButton = ({ wsPlayerId, playerId, tsCoachId }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const wrapRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [onRadar, setOnRadar] = useState(false);
  const [savedListIds, setSavedListIds] = useState([]);
  const [lists, setLists] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftListIds, setDraftListIds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [hover, setHover] = useState(false);

  const hasId = wsPlayerId != null || playerId != null || tsCoachId != null;

  useEffect(() => {
    ensureAnim();
  }, []);

  // Initial radar status for this player.
  useEffect(() => {
    if (!hasId) return;
    const controller = new AbortController();
    const params = {};
    if (wsPlayerId != null) params.ws_player_id = wsPlayerId;
    else if (playerId != null) params.player_id = playerId;
    else params.ts_coach_id = tsCoachId;
    (async () => {
      try {
        const res = await agentRadarService.getProfileStatus(
          params,
          controller.signal
        );
        if (res.success) {
          setOnRadar(!!res.data?.on_radar);
          setSavedListIds(res.data?.list_ids || []);
          setLists(res.data?.lists || []);
          setLoaded(true);
        }
      } catch (e) {
        /* aborted on unmount — ignore */
      }
    })();
    return () => controller.abort();
  }, [wsPlayerId, playerId, tsCoachId, hasId]);

  const commit = useCallback(
    (finalIds) => {
      setPickerOpen(false);
      if (sameSet(finalIds, savedListIds)) return; // nothing changed
      const prevSaved = savedListIds;
      const prevOnRadar = onRadar;
      // Optimistic button-state update.
      setSavedListIds(finalIds);
      setOnRadar(finalIds.length > 0);
      agentRadarService
        .saveFromProfile({ wsPlayerId, playerId, tsCoachId, listIds: finalIds })
        .then((res) => {
          if (!res.success) {
            setSavedListIds(prevSaved);
            setOnRadar(prevOnRadar);
            toast.error(res.error || "Couldn't update your radar.");
          }
        });
    },
    [savedListIds, onRadar, wsPlayerId, playerId, tsCoachId]
  );

  const openPicker = () => {
    setDraftListIds([...savedListIds]);
    setHover(false);
    setPickerOpen(true);
  };

  const toggle = (listId) =>
    setDraftListIds((cur) =>
      cur.includes(listId) ? cur.filter((x) => x !== listId) : cur.concat(listId)
    );

  const createList = async (name) => {
    setCreating(true);
    const res = await agentRadarService.createList(name);
    setCreating(false);
    if (!res.success || !res.data?.id) {
      toast.error(res.error || "Couldn't create the list.");
      return false;
    }
    setLists((cur) => cur.concat(res.data));
    setDraftListIds((cur) => cur.concat(res.data.id)); // create + auto-check
    return true;
  };

  const done = () => commit(draftListIds);

  // Desktop: clicking outside the button+popover commits & closes (matches the
  // prototype, where toggles persist). Mobile close is the sheet scrim (in-picker).
  useEffect(() => {
    if (!pickerOpen || isMobile) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        commit(draftListIds);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pickerOpen, isMobile, draftListIds, commit]);

  if (!hasId || !loaded) return null;

  const baseBtn = {
    borderRadius: "9px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    ...(isMobile ? { minHeight: "44px" } : {}),
  };

  const button = onRadar ? (
    <button
      type="button"
      onClick={openPicker}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-haspopup="true"
      aria-expanded={pickerOpen}
      style={{
        ...baseBtn,
        background: hover ? "#F8FAFC" : "#FFFFFF",
        border: "1px solid #DDE3EB",
        color: "#111827",
        padding: "9px 16px",
        display: "flex",
        alignItems: "center",
        gap: "7px",
      }}
    >
      <span style={{ color: "#16A34A", fontWeight: 800 }}>✓</span>On your radar
    </button>
  ) : (
    <button
      type="button"
      onClick={openPicker}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-haspopup="true"
      aria-expanded={pickerOpen}
      style={{
        ...baseBtn,
        background: hover ? "#1B6FE0" : "#2283FF",
        color: "#FFFFFF",
        border: "none",
        padding: "10px 17px",
      }}
    >
      Add to Radar
    </button>
  );

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>
      {button}
      {pickerOpen && (
        <SaveToListPicker
          isMobile={isMobile}
          lists={lists}
          draftListIds={draftListIds}
          onToggle={toggle}
          onCreateList={createList}
          creating={creating}
          onDone={done}
        />
      )}
    </div>
  );
};

export default AddToRadarButton;
